import { useState, useRef, useEffect } from 'react'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/src/style.css'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pad2(n: number) { return String(n).padStart(2, '0') }

function parseDateOnly(str: string): string {
  return str?.length >= 10 ? str.slice(0, 10) : ''
}

function parseMinutes(str: string): number | null {
  if (!str || str.length < 16) return null
  const timePart = str.slice(11, 16)
  const [hStr, mStr] = timePart.split(':')
  const h = parseInt(hStr, 10)
  const m = parseInt(mStr, 10)
  if (isNaN(h) || isNaN(m)) return null
  return h * 60 + m
}

function toDateTimeStr(dateStr: string, minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${dateStr}T${pad2(h)}:${pad2(m)}`
}

function parseDate(dateStr: string): Date | undefined {
  if (!dateStr) return undefined
  const d = new Date(`${dateStr}T12:00`)
  return isNaN(d.getTime()) ? undefined : d
}

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function displayDate(dateStr: string): string {
  const d = parseDate(dateStr)
  if (!d) return 'Pick a date'
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatTime(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return `${h12}:${pad2(m)} ${ampm}`
}

function durationLabel(startMin: number, endMin: number): string {
  const diff = endMin - startMin
  if (diff <= 0) return ''
  const h = Math.floor(diff / 60)
  const m = diff % 60
  if (h === 0) return `${m} min`
  if (m === 0) return `${h} hr`
  return `${h} hr ${m} min`
}

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => i * 30)

// ─── Shared popover hook ──────────────────────────────────────────────────────

function usePopover() {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  return { open, setOpen, containerRef }
}

// ─── Shared calendar popover style (must stay inline for DayPicker CSS vars) ─

const calPopoverStyle: React.CSSProperties = {
  position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 200,
  background: 'var(--surface)', border: '1px solid var(--line-soft)',
  borderRadius: 16, boxShadow: '0 8px 32px rgba(60,40,10,0.14)',
  padding: '12px 12px 14px',
  ['--rdp-accent-color' as string]: 'var(--primary)',
  ['--rdp-accent-background-color' as string]: 'var(--primary-soft)',
  ['--rdp-day-height' as string]: '38px',
  ['--rdp-day-width' as string]: '38px',
  ['--rdp-day_button-height' as string]: '36px',
  ['--rdp-day_button-width' as string]: '36px',
}

const chipCls = 'h-[38px] px-[11px] rounded-lg border border-line-soft bg-s2 text-[13.5px] font-sans text-ink cursor-pointer flex items-center gap-1.5 whitespace-nowrap outline-none'
const timeSelCls = 'h-[38px] px-1 rounded-lg border border-line-soft bg-s2 text-[13.5px] font-sans text-ink cursor-pointer outline-none'

// ─── DatePicker (date-only — used for recurrence end date) ───────────────────

interface DatePickerProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  id?: string
  'aria-label'?: string
  'aria-invalid'?: boolean
  style?: React.CSSProperties
}

export function DatePicker({ value, onChange, placeholder, id, 'aria-label': _ariaLabel, 'aria-invalid': ariaInvalid, style }: DatePickerProps) {
  const selected = parseDate(value)
  const { open, setOpen, containerRef } = usePopover()

  function handleDaySelect(day: Date | undefined) {
    if (!day) return
    onChange(toDateStr(day))
    setOpen(false)
  }

  const label = selected ? displayDate(value) : (placeholder ?? 'Pick a date')

  return (
    <div ref={containerRef} className="relative" style={style}>
      <button
        type="button"
        id={id}
        aria-haspopup="dialog"
        aria-invalid={ariaInvalid}
        onClick={() => setOpen(!open)}
        className={`${chipCls} w-full h-11 justify-start`}
        style={{
          borderColor: ariaInvalid ? '#A33A2A' : 'var(--line-soft)',
          color: selected ? 'var(--ink)' : 'var(--muted)',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted shrink-0">
          <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span className="overflow-hidden text-ellipsis whitespace-nowrap">{label}</span>
      </button>

      {open && (
        <div role="dialog" aria-label="Date picker" style={calPopoverStyle}>
          <DayPicker
            mode="single"
            selected={selected}
            onSelect={handleDaySelect}
            showOutsideDays
            defaultMonth={selected}
          />
        </div>
      )}
    </div>
  )
}

// ─── EventTimePicker ──────────────────────────────────────────────────────────

interface EventTimePickerProps {
  startsAt: string
  endsAt: string
  onStartsAtChange: (v: string) => void
  onEndsAtChange: (v: string) => void
  startsAtError?: string
}

export function EventTimePicker({ startsAt, endsAt, onStartsAtChange, onEndsAtChange, startsAtError }: EventTimePickerProps) {
  const startDate = parseDateOnly(startsAt)
  const endDate = parseDateOnly(endsAt)
  const startMin = parseMinutes(startsAt) ?? 660   // default 11:00 AM
  const endMin = parseMinutes(endsAt)

  const isMultiDay = !!endDate && endDate !== startDate
  const [showMultiDay, setShowMultiDay] = useState(isMultiDay)

  const startPopover = usePopover()
  const endPopover = usePopover()

  const selectedStartDate = parseDate(startDate)
  const selectedEndDate = parseDate(endDate)

  function handleStartDaySelect(day: Date | undefined) {
    if (!day) return
    const dateStr = toDateStr(day)
    onStartsAtChange(toDateTimeStr(dateStr, startMin))
    // Auto-set end to start day + 1 hr if no end exists
    if (!endsAt) {
      onEndsAtChange(toDateTimeStr(dateStr, Math.min(startMin + 60, 1410)))
    } else if (!showMultiDay) {
      // Keep end on same day when not multi-day
      onEndsAtChange(toDateTimeStr(dateStr, endMin ?? Math.min(startMin + 60, 1410)))
    }
    startPopover.setOpen(false)
  }

  function handleStartTimeChange(newMin: number) {
    const dateStr = startDate || toDateStr(new Date())
    onStartsAtChange(toDateTimeStr(dateStr, newMin))
    // Auto-advance end if on same day and end ≤ new start
    if (!showMultiDay) {
      const currentEnd = endMin ?? newMin + 60
      if (currentEnd <= newMin) {
        const endDateStr = endDate || dateStr
        onEndsAtChange(toDateTimeStr(endDateStr, Math.min(newMin + 60, 1410)))
      }
    }
    // If no end at all, set it
    if (!endsAt) {
      onEndsAtChange(toDateTimeStr(dateStr, Math.min(newMin + 60, 1410)))
    }
  }

  function handleEndTimeChange(newMin: number) {
    const endDateStr = showMultiDay ? (endDate || startDate) : startDate
    if (!endDateStr) return
    onEndsAtChange(toDateTimeStr(endDateStr, newMin))
  }

  function handleEndDaySelect(day: Date | undefined) {
    if (!day) return
    const dateStr = toDateStr(day)
    onEndsAtChange(toDateTimeStr(dateStr, endMin ?? Math.min(startMin + 60, 1410)))
    endPopover.setOpen(false)
  }

  function toggleMultiDay() {
    if (showMultiDay) {
      // Collapse: move end date to same day as start
      if (startDate) {
        onEndsAtChange(toDateTimeStr(startDate, endMin ?? Math.min(startMin + 60, 1410)))
      }
      setShowMultiDay(false)
    } else {
      setShowMultiDay(true)
    }
  }

  const resolvedEndMin = endMin ?? Math.min(startMin + 60, 1410)
  const durLabel = !showMultiDay && endMin !== null && endMin > startMin
    ? durationLabel(startMin, endMin)
    : ''

  return (
    <div>
      <div className="flex items-center gap-1.5 flex-wrap">

        {/* ── Start date chip ────────────────────────────────────────── */}
        <div ref={startPopover.containerRef} className="relative">
          <button
            type="button"
            aria-haspopup="dialog"
            aria-invalid={Boolean(startsAtError)}
            onClick={() => startPopover.setOpen(!startPopover.open)}
            className={chipCls}
            style={{
              borderColor: startsAtError ? '#A33A2A' : 'var(--line-soft)',
              color: startDate ? 'var(--ink)' : 'var(--muted)',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted shrink-0">
              <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {startDate ? displayDate(startDate) : 'Pick a date'}
          </button>

          {startPopover.open && (
            <div role="dialog" aria-label="Start date" style={calPopoverStyle}>
              <DayPicker
                mode="single"
                selected={selectedStartDate}
                onSelect={handleStartDaySelect}
                showOutsideDays
                defaultMonth={selectedStartDate}
              />
            </div>
          )}
        </div>

        {/* ── Start time ─────────────────────────────────────────────── */}
        <select
          aria-label="Start time"
          value={startMin}
          onChange={(e) => handleStartTimeChange(parseInt(e.target.value))}
          className={timeSelCls}
        >
          {TIME_OPTIONS.map(m => (
            <option key={m} value={m}>{formatTime(m)}</option>
          ))}
        </select>

        <span className="text-muted text-[13px] select-none">→</span>

        {/* ── End date chip (multi-day only) ──────────────────────────── */}
        {showMultiDay && (
          <div ref={endPopover.containerRef} className="relative">
            <button
              type="button"
              aria-haspopup="dialog"
              onClick={() => endPopover.setOpen(!endPopover.open)}
              className={chipCls}
              style={{ color: (endDate && endDate !== startDate) ? 'var(--ink)' : 'var(--muted)' }}
            >
              {(endDate && endDate !== startDate) ? displayDate(endDate) : 'Pick end date'}
            </button>

            {endPopover.open && (
              <div role="dialog" aria-label="End date" style={calPopoverStyle}>
                <DayPicker
                  mode="single"
                  selected={selectedEndDate}
                  onSelect={handleEndDaySelect}
                  showOutsideDays
                  defaultMonth={selectedEndDate ?? selectedStartDate}
                />
              </div>
            )}
          </div>
        )}

        {/* ── End time ───────────────────────────────────────────────── */}
        <select
          aria-label="End time"
          value={resolvedEndMin}
          onChange={(e) => handleEndTimeChange(parseInt(e.target.value))}
          className={timeSelCls}
        >
          {TIME_OPTIONS.map(m => (
            <option key={m} value={m}>{formatTime(m)}</option>
          ))}
        </select>

        {/* ── Duration hint ──────────────────────────────────────────── */}
        {durLabel && (
          <span className="text-[12.5px] text-muted select-none">· {durLabel}</span>
        )}
      </div>

      {/* ── Error ──────────────────────────────────────────────────────── */}
      {startsAtError && (
        <div className="text-[11.5px] text-[#A33A2A] mt-1.5">{startsAtError}</div>
      )}

      {/* ── Multi-day toggle ───────────────────────────────────────────── */}
      <button
        type="button"
        onClick={toggleMultiDay}
        className="mt-2 bg-transparent border-0 p-0 text-[12.5px] text-primary cursor-pointer font-sans inline-flex items-center gap-1"
      >
        {showMultiDay ? '− Same day' : '＋ Ends on a different day'}
      </button>
    </div>
  )
}
